import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["avatar", "checkbox", "checked", "defaultValue", "description", "detail", "disabled", "focused", "hovered", "id", "isLast", "keyboardFocused", "label", "leftIcon", "link", "logo", "logoProps", "onClick", "onPress", "onToggle", "pressed", "radio", "rightText", "rightIcon", "text", "toggleStatus", "value", "isRightTextEllipsis", "detailStatus", "UNSAFE_touchableProps", "name", "testID"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import View from '@bubble-ui/primitive-view';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import InteractiveSelectorBackground from '@bubble-ui/atom-interactive-selector-background';
import RadioBullet from '@bubble-ui/atom-radio-bullet';
import SelectorBottomSeparator from '@bubble-ui/atom-selector-bottom-separator';
import SwitchToggle from '@bubble-ui/molecule-switch-toggle';
import Text from '@bubble-ui/primitive-text';
import Touchable from '@bubble-ui/primitive-touchable';
import TransactionCheckmark from '@bubble-ui/atom-transaction-checkmark';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { useDesignContext } from '@klarna/designtokens';
import defaultColorVariables from '@bubble-ui/particle-default-color-variables';
import defaultTextStyles from '@bubble-ui/particle-default-text-styles';
import { useUniqueFormIdentifier } from '@bubble-ui/hook-use-unique-form-identifier';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
var BaseOption = _ref => {
  var {
      avatar,
      checkbox,
      checked,
      defaultValue,
      description,
      detail,
      disabled,
      focused,
      hovered,
      id,
      isLast,
      keyboardFocused,
      label,
      leftIcon,
      link,
      logo,
      logoProps,
      onClick,
      onPress,
      onToggle,
      pressed,
      radio,
      rightText,
      rightIcon,
      text,
      toggleStatus,
      value,
      isRightTextEllipsis = true,
      detailStatus,
      UNSAFE_touchableProps,
      name,
      testID
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  var designContext = useDesignContext(_objectSpread(_objectSpread({}, defaultColorVariables), defaultTextStyles));
  var designVersion = useDesignVersion();
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombined({
    disabled,
    focused,
    hovered,
    keyboardFocused,
    pressed
  });
  var ids = getIds(id);
  var {
    inputStyle,
    avatarStyle,
    touchableStyle,
    containerStyle,
    leftIconStyle,
    rightIconStyle,
    rightTextWrapperStyle,
    rightTextStyle,
    checkboxStyle,
    radioStyle,
    toggleStyle,
    logoStyle,
    textStyle,
    descriptionStyle,
    textWrapperStyle,
    outerTextWrapperStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(BaseOption, getDefaultStyleSheet, _objectSpread(_objectSpread({}, props), {}, {
    avatar,
    checkbox,
    checked,
    defaultValue,
    description,
    designContext,
    designVersion,
    detail,
    detailStatus,
    disabled: interactiveStateCombined.disabled,
    id,
    isLast,
    isRightTextEllipsis,
    label,
    leftIcon,
    link,
    logo,
    logoProps,
    name,
    onClick,
    onPress,
    onToggle,
    radio,
    rightText,
    rightIcon,
    testID,
    text,
    toggleStatus,
    UNSAFE_touchableProps,
    value
  }));
  var componentName = useUniqueFormIdentifier({
    name,
    fallbackName: 'SelectorOptions'
  });
  var Content = /*#__PURE__*/React.createElement(View, {
    style: containerStyle
  }, !interactiveStateCombined.disabled && !onToggle && /*#__PURE__*/React.createElement(InteractiveSelectorBackground, {
    focused: interactiveStateCombined.keyboardFocused,
    pressed: interactiveStateCombined.pressed,
    hovered: interactiveStateCombined.hovered
  }), leftIcon ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.leftIcon,
    style: leftIconStyle
  }, leftIcon) : avatar ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.avatar,
    style: avatarStyle
  }, avatar) : logo ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.logo,
    style: logoStyle
  }, logo) : undefined, /*#__PURE__*/React.createElement(View, {
    style: outerTextWrapperStyle
  }, /*#__PURE__*/React.createElement(View, {
    style: textWrapperStyle
  }, /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.text,
    style: textStyle
  }, text), rightText ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.rightText,
    style: rightTextWrapperStyle
  }, /*#__PURE__*/React.createElement(Text, {
    style: rightTextStyle,
    numberOfLines: 1
  }, rightText)) : detail ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.detail,
    style: rightTextWrapperStyle
  }, /*#__PURE__*/React.createElement(Text, {
    style: rightTextStyle,
    numberOfLines: 1
  }, detail)) : link ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.link,
    style: rightTextWrapperStyle
  }, /*#__PURE__*/React.createElement(Text, {
    style: rightTextStyle,
    numberOfLines: 1
  }, link)) : undefined), description && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.description,
    style: descriptionStyle
  }, description)), !detail && !link && (checkbox ? /*#__PURE__*/React.createElement(View, {
    tabIndex: -1,
    style: checkboxStyle
  }, /*#__PURE__*/React.createElement(TransactionCheckmark, {
    disabled: interactiveStateCombined.disabled,
    focused: interactiveStateCombined.keyboardFocused,
    hovered: interactiveStateCombined.hovered,
    isChecked: checked,
    pressed: interactiveStateCombined.pressed
  })) : onToggle ? /*#__PURE__*/React.createElement(View, {
    tabIndex: -1,
    style: toggleStyle
  }, /*#__PURE__*/React.createElement(SwitchToggle, {
    name: label || componentName,
    id: id,
    disabled: interactiveStateCombined.disabled,
    pressed: interactiveStateCombined.pressed,
    hovered: interactiveStateCombined.hovered,
    onChange: onToggle,
    error: toggleStatus === 'error',
    warning: toggleStatus === 'warning',
    checked: checked,
    defaultValue: typeof defaultValue === 'boolean' ? defaultValue : undefined
  })) : rightIcon ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.rightIcon,
    style: rightIconStyle
  }, rightIcon) : undefined), isLast || /*#__PURE__*/React.createElement(SelectorBottomSeparator, _extends({
    hasIcon: Boolean(leftIcon)
  }, logo ? {
    hasLogo: true,
    logoSize: logoProps !== null && logoProps !== void 0 && logoProps.small ? 'small' : logoProps !== null && logoProps !== void 0 && logoProps.medium ? 'medium' : undefined
  } : {})));
  return /*#__PURE__*/React.createElement(React.Fragment, null, radio ? /*#__PURE__*/React.createElement(View, _extends({
    testID: testID
  }, UNSAFE_touchableProps, {
    style: touchableStyle
  }, props, eventHandlers), /*#__PURE__*/React.createElement("label", {
    id: ids === null || ids === void 0 ? void 0 : ids.label
  }, /*#__PURE__*/React.createElement("input", {
    style: inputStyle,
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    name: componentName,
    type: "radio",
    checked: checked,
    value: value,
    disabled: interactiveStateCombined.disabled,
    onChange: e => onClick === null || onClick === void 0 ? void 0 : onClick(value, e)
  }), /*#__PURE__*/React.createElement(View, {
    style: containerStyle
  }, !interactiveStateCombined.disabled && !onToggle && /*#__PURE__*/React.createElement(InteractiveSelectorBackground, {
    focused: interactiveStateCombined.keyboardFocused,
    pressed: interactiveStateCombined.pressed,
    hovered: interactiveStateCombined.hovered
  }), leftIcon ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.leftIcon,
    style: leftIconStyle
  }, leftIcon) : avatar ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.avatar,
    style: avatarStyle
  }, avatar) : logo ? /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.logo,
    style: logoStyle
  }, logo) : undefined, /*#__PURE__*/React.createElement(View, {
    style: outerTextWrapperStyle
  }, /*#__PURE__*/React.createElement(View, {
    style: textWrapperStyle
  }, /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.text,
    style: textStyle
  }, text)), description && /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.description,
    style: descriptionStyle
  }, description)), /*#__PURE__*/React.createElement(View, {
    tabIndex: -1,
    style: radioStyle
  }, /*#__PURE__*/React.createElement(RadioBullet, {
    excludeInput: true,
    checked: checked,
    disabled: interactiveStateCombined.disabled,
    keyboardFocused: interactiveStateCombined.keyboardFocused,
    hovered: interactiveStateCombined.hovered
  })), isLast || /*#__PURE__*/React.createElement(SelectorBottomSeparator, _extends({
    hasIcon: Boolean(leftIcon)
  }, logo ? {
    hasLogo: true,
    logoSize: logoProps !== null && logoProps !== void 0 && logoProps.small ? 'small' : logoProps !== null && logoProps !== void 0 && logoProps.medium ? 'medium' : undefined
  } : {}))))) : onToggle ? /*#__PURE__*/React.createElement(View, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    testID: testID
  }, UNSAFE_touchableProps, {
    style: touchableStyle
  }, props, eventHandlers), Content) : /*#__PURE__*/React.createElement(Touchable, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    testID: testID
  }, UNSAFE_touchableProps, {
    disabled: interactiveStateCombined.disabled,
    onClick: e => {
      onClick === null || onClick === void 0 ? void 0 : onClick(value, e);
    },
    onPress: e => {
      onClick === null || onClick === void 0 ? void 0 : onClick(value, e);
    },
    style: touchableStyle,
    value: value,
    name: name
  }, props, eventHandlers), Content));
};
BaseOption.displayName = 'SelectorOptionBase';
export default withProvisionalOverrideFromContext(BaseOption);
//# sourceMappingURL=index.js.map